import React, { Suspense, useCallback } from 'react'
import MainLayout from '../layouts/MainLayout'
import { TabStack, TabContent } from './TabStack'
import { useTabs, useActiveTab } from '../contexts/TabManagerContext'
import { CanvasFocusProvider } from '../contexts/CanvasFocusContext'
import { useFocusRecovery } from '../lib/focus'
import { useAutoBackup } from '../hooks/useAutoBackup'
import { useMainWindowReady } from '../hooks/useMainWindowReady'
import type { Tab } from '../hooks/multiWindow'

/**
 * Renders every open tab's content in parallel via `<TabStack>`. Consumed
 * from the `TabManagerContext` that `MainLayout` sets up — the hook is only
 * called once (inside MainLayout), so all consumers share the same tab
 * state. Inactive tabs stay mounted but hidden, making tab switches
 * instant (no remount, no list flash, preserved scroll + selection).
 */
const TabStackHost: React.FC = () => {
  const { tabs } = useTabs()
  const { activeTabId } = useActiveTab()
  // Stable render-prop so `React.memo(TabStack)` can bail when tabs/activeId
  // are unchanged. An inline arrow would create a new function reference on
  // every render and defeat the memo. `isActive` is not threaded as a prop
  // anymore — TabStack wraps each slot in an `ActiveTabProvider` so pages
  // subscribe directly via `useIsActiveTab()`.
  const renderTab = useCallback(
    (tab: Tab) => <TabContent tab={tab} />,
    [],
  )
  // Ohne Tabs steht hier nichts — nicht einmal einen Frame lang ein
  // Platzhalter. Der Tab-Manager öffnet in jedem erreichbaren Fall (Start,
  // Wiederherstellen, Bereichswechsel, Schließen, Aufräumen nach dem Sync)
  // einen Standard-Tab; leer ist die Liste nur für einen Augenblick beim Start.
  if (tabs.length === 0) return null
  return (
    <TabStack tabs={tabs} activeTabId={activeTabId}>
      {renderTab}
    </TabStack>
  )
}

const HomeLayout: React.FC = () => {
  // Räumt einen Fokus auf, den ein Absturz ohne Ende zurückgelassen hat —
  // genau einmal, im Hauptfenster (die Pille ist ja der Fall, um den es geht).
  useFocusRecovery()
  // Einmal am Tag ein Backup (#158) — nur am Desktop, nur in Fenstern mit der App selbst.
  useAutoBackup()
  // Ein per Doppelklick geöffnetes Backup wartet, bis die App steht — nicht schon im Setup.
  useMainWindowReady()

  return (
    // Sits ABOVE MainLayout so the layout itself can read the flag — the
    // Projekt-Canvas (deep inside TabStack) sets it, MainLayout reacts by
    // clearing the left navigation.
    <CanvasFocusProvider>
      <MainLayout>
        <Suspense fallback={null}>
          <TabStackHost />
        </Suspense>
      </MainLayout>
    </CanvasFocusProvider>
  )
}

export default HomeLayout